import React, { memo, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { Alert, Button, Skeleton, Stack } from 'investira.react.components';
import { CorrelacaoMatriz, CorrelacaoHeatMap } from '../../../components/organisms';
import services from '../../../services';
import utils from '../../../utils';

const wCorrelationDays = 252;

/** Informa se a resposta trouxe uma coleção com dados utilizáveis. */
function pvHasCorrelationData(pValue) {
  if (Array.isArray(pValue)) {
    return pValue.length > 0;
  }

  return Boolean(pValue && typeof pValue === 'object' && Object.keys(pValue).length);
}

/**
 * Consulta a matriz de correlação com o mesmo payload praticado pela tela de
 * Correlações, preservando o ciclo de dados isolado deste bloco.
 */
const ComparadorFundosCorrelacao = memo(({ fundos = [] }) => {
  const [state, setState] = useState({ status: 'idle', data: null, error: '' });
  const [retryVersion, setRetryVersion] = useState(0);
  const xAtivoIds = fundos.map(pFundo => pFundo.ifeed_ativo_id).filter(Boolean);
  const xAtivoIdsKey = xAtivoIds.join(',');

  useEffect(() => {
    let xActive = true;

    if (xAtivoIds.length < 2) {
      setState({ status: 'empty', data: null, error: '' });
      return undefined;
    }

    setState({ status: 'loading', data: null, error: '' });
    const xDataAtual = dayjs();
    const xMesesAnteriores = xDataAtual.date() > 15 ? 1 : 2;

    services.consultas.correlacao(
      {
        data: {
          data: utils.dates.lastMonthDayValid(),
          dias: wCorrelationDays,
          incluir_variacoes: true,
          itens: xAtivoIds.map(pAtivoId => ({ ativo_id: Number(pAtivoId) })),
          corridos: false
        }
      },
      pResponse => {
        const xData = pResponse?.data || pResponse;

        if (!xActive) {
          return;
        }

        if (!pvHasCorrelationData(xData?.matriz) || !pvHasCorrelationData(xData?.itens)) {
          setState({ status: 'empty', data: null, error: '' });
          return;
        }

        setState({ status: 'ready', data: xData, error: '' });
      },
      pError => {
        if (xActive) {
          setState({
            status: 'error',
            data: null,
            error: pError?.message || 'Fonte indisponível.'
          });
        }
      }
    );

    return () => {
      xActive = false;
    };
  }, [retryVersion, xAtivoIdsKey]);

  if (state.status === 'loading' || state.status === 'idle') {
    return <Skeleton aria-label="Carregando correlação" />;
  }

  if (state.status === 'error') {
    return (
      <Alert
        severity="error"
        action={
          <Button size="small" onClick={() => setRetryVersion(pVersion => pVersion + 1)}>
            Tentar novamente
          </Button>
        }>
        Não foi possível carregar a correlação: {state.error}
      </Alert>
    );
  }

  if (state.status === 'empty') {
    return <Alert severity="info">Série insuficiente para calcular a correlação.</Alert>;
  }

  return (
    <Stack spacing={2}>
      <CorrelacaoHeatMap correlacoes={state.data.correlacoes} itens={state.data.itens} />
    </Stack>
  );
});

export default ComparadorFundosCorrelacao;
